import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import StatsCards from './components/StatsCards';
import DashboardCharts from './components/DashboardCharts';
import DashboardLists from './components/DashboardLists';

interface AlertItem {
  type: 'receipt' | 'teacher' | 'hakedis';
  label: string;
  count: number;
}

export default function AdminDashboard() {
  const today = new Date().toLocaleDateString('tr-TR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  useEffect(() => {
    const fetchAlerts = async () => {
      const todayStr = new Date().toISOString().split('T')[0];
      const monthStart = todayStr.slice(0, 7) + '-01';

      const [
        { count: pendingReceipts },
        { count: missingTeacher },
        { count: outdatedHakedis },
      ] = await Promise.all([
        supabase.from('egtpanel_odemeler').select('id', { count: 'exact', head: true }).eq('yonetici_onay', false).neq('odeme_durumu', 'İptal'),
        supabase.from('egtpanel_ogrenciler').select('id', { count: 'exact', head: true }).eq('aktif', true).is('atanan_ogretmen_id', null),
        supabase.from('egtpanel_hak_edis_odemeleri').select('id', { count: 'exact', head: true }).lt('odeme_tarihi', monthStart).eq('odeme_durumu', 'Bekliyor'),
      ]);

      const items: AlertItem[] = [];
      if ((pendingReceipts ?? 0) > 0) items.push({ type: 'receipt', label: 'bekleyen dekont onayı', count: pendingReceipts ?? 0 });
      if ((missingTeacher ?? 0) > 0) items.push({ type: 'teacher', label: 'başvuru için öğretmen bulunmadı', count: missingTeacher ?? 0 });
      if ((outdatedHakedis ?? 0) > 0) items.push({ type: 'hakedis', label: 'hak ediş hesabı güncel değil', count: outdatedHakedis ?? 0 });
      setAlerts(items);
    };
    fetchAlerts();
  }, []);

  const totalAlerts = alerts.reduce((a, i) => a + i.count, 0);
  const alertText = alerts.map(a => `${a.count} adet ${a.label}`).join(' · ');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Operasyon Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">{today}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-xl">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
            <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap">Canlı Takip Aktif</span>
          </div>
          <button className="flex items-center gap-2 bg-[#1a2f5a] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#243d72] transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" />
            Yeni Başvuru
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards />

      {/* Alert Banner */}
      {totalAlerts > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3.5 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
            <i className="ri-alarm-warning-line text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">Dikkat: {totalAlerts} acil işlem bekliyor</p>
            <p className="text-xs text-amber-600 mt-0.5">
              {alertText}
            </p>
          </div>
          <button className="text-xs font-semibold text-amber-700 bg-amber-100 px-3 py-1.5 rounded-lg hover:bg-amber-200 transition-colors cursor-pointer whitespace-nowrap">
            İncele
          </button>
        </div>
      )}

      {/* Charts */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-bar-chart-2-line text-[#1a2f5a]" />
          Analitik & Grafikler
        </h2>
        <DashboardCharts />
      </div>

      {/* Lists */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-list-check-2 text-[#1a2f5a]" />
          Günlük Operasyon Takibi
        </h2>
        <DashboardLists />
      </div>
    </div>
  );
}